import React from 'react';
import Button from 'antd/lib/button';
import Select from 'antd/lib/select';
import Tooltip from 'antd/lib/tooltip';

import { plural, t } from 'utils/i18n';
import { detectionOf, StateLike } from './references/reference-model';
import LibraryThumb from './references/library-thumb';
import { CardPreview, hasPreviewSpec, previewsOf } from './model-card';
import type { PinKey } from './preview-mode';
import './references/styles.scss';
import './styles.scss';

interface Props {
    states: StateLike[]; // the objects of the current drawing
    backendAPI: string;
    pinned: PinKey | null;
    onHover(state: StateLike): void; // activates it on the canvas, as the Objects tab does
    onPin(state: StateLike): void; // shows its card on the canvas
    onChange(state: StateLike, all: boolean): void; // opens the model picker, for its whole label when all
}

interface Row { state: StateLike; previews: CardPreview[]; names: string[] }

function isPinned(state: StateLike, key: PinKey | null): boolean {
    if (!key) return false;
    return key.serverID !== null ? state.serverID === key.serverID : state.clientID === key.clientID;
}

function hasPool(state: StateLike): boolean {
    return (detectionOf(state)?.candidates.length ?? 0) > 0;
}

// a label's objects, one line per model they share; the objects without one come last
function byModel(rows: Row[]): { key: string; rows: Row[] }[] {
    const groups = new Map<string, Row[]>();
    rows.forEach((row) => {
        const key = row.names.join(' · ');
        groups.set(key, [...(groups.get(key) ?? []), row]);
    });
    return [...groups.entries()]
        .map(([key, items]) => ({ key, rows: items }))
        .sort((a, b) => Number(!a.key) - Number(!b.key));
}

// the furniture of the drawing by category, with the models it got from the library
export default function PreviewPanel(props: Props): JSX.Element {
    const {
        states, backendAPI, pinned, onHover, onPin, onChange,
    } = props;
    const rows: Row[] = states
        .filter(hasPreviewSpec)
        .sort((a, b) => a.clientID - b.clientID)
        .map((state) => {
            const previews = previewsOf(state, backendAPI);
            const names = previews.map((preview) => preview.name).filter((name): name is string => !!name);
            return { state, previews, names };
        });
    const missing = rows.filter((row) => !row.names.length).length;

    if (!rows.length) {
        return (
            <div className='cvat-references-panel'>
                <span className='cvat-references-empty'>{t('No furniture on this drawing')}</span>
            </div>
        );
    }

    const category = (label: string): JSX.Element => {
        const all = rows.filter((row) => row.state.label.name === label);
        const groups = byModel(all);
        const withPool = all.filter((row) => hasPool(row.state));
        // the picker shows the pool of the object it opens for: the pinned one when it is of this label
        const target = withPool.find((row) => isPinned(row.state, pinned)) ?? withPool[0];
        let action: JSX.Element | null = null;
        if (target) {
            let text = target.names.length ? t('Change') : t('Pick');
            if (all.length > 1) text = t('Change for all');
            action = (
                <Button type='link' size='small' onClick={() => onChange(target.state, all.length > 1)}>
                    {text}
                </Button>
            );
        } else if (detectionOf(all[0].state)) {
            action = <span className='cvat-model-card-note'>{t('No matches from the photos')}</span>;
        }
        return (
            <div key={label} className='cvat-preview-category'>
                <div className='cvat-preview-category-head'>
                    <strong>{label}</strong>
                    <span className='cvat-references-photo-label'>{plural(all.length, 'object')}</span>
                    {action}
                </div>
                {groups.map((group) => {
                    const [first] = group.rows[0].previews;
                    const here = group.rows.find((row) => isPinned(row.state, pinned));
                    const classes = ['cvat-preview-model'];
                    if (here) classes.push('cvat-preview-model-selected');
                    return (
                        <div key={group.key} className={classes.join(' ')}>
                            <LibraryThumb
                                className='cvat-references-model-thumb'
                                src={first.imageUrl}
                                fullSrc={first.fullImageUrl}
                            />
                            <div className='cvat-references-row-body'>
                                {group.key ? (
                                    <Tooltip title={group.key}>
                                        <span className='cvat-references-model-name'>{group.key}</span>
                                    </Tooltip>
                                ) : (
                                    <span className='cvat-references-no-model'>{t('No model yet')}</span>
                                )}
                                {/* a label can have a hundred objects: a list of ids, the canvas is the map */}
                                <Select
                                    size='small'
                                    showSearch
                                    className='cvat-preview-ids'
                                    placeholder={plural(group.rows.length, 'object')}
                                    value={here ? here.state.clientID : null}
                                    onChange={(clientID: number) => {
                                        const row = group.rows.find((item) => item.state.clientID === clientID);
                                        if (row) onPin(row.state);
                                    }}
                                >
                                    {group.rows.map(({ state }) => (
                                        <Select.Option key={state.clientID} value={state.clientID}>
                                            <div onMouseEnter={() => onHover(state)}>{state.clientID}</div>
                                        </Select.Option>
                                    ))}
                                </Select>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className='cvat-references-panel'>
            <div className='cvat-references-header'>
                <div className='cvat-references-summary'>
                    {t('{done} of {total} have a model from the library', { done: rows.length - missing, total: rows.length })}
                </div>
            </div>
            <div className='cvat-references-list'>
                {[...new Set(rows.map((row) => row.state.label.name))].sort().map(category)}
            </div>
        </div>
    );
}
